import React from "react";

import "../../styles/hrPageShell.css";
function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString();
}

function normalizeKey(key) {
  return String(key || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function getField(record, ...candidateNames) {
  if (!record || typeof record !== "object") return "";

  for (const name of candidateNames) {
    if (Object.prototype.hasOwnProperty.call(record, name)) {
      return record[name];
    }
  }

  const lookup = Object.keys(record).reduce((acc, key) => {
    acc[normalizeKey(key)] = record[key];
    return acc;
  }, {});

  for (const name of candidateNames) {
    const normalizedName = normalizeKey(name);
    if (Object.prototype.hasOwnProperty.call(lookup, normalizedName)) {
      return lookup[normalizedName];
    }
  }

  return "";
}

function normalizeHistoryRow(row = {}) {
  return {
    id: String(getField(row, "id", "ID", "ItemInternalId") || ""),
    employeeCode: getField(row, "employeeCode", "EmployeeCode", "field_1"),
    leavePolicyId: getField(row, "leavePolicyId", "LeavePolicyId", "field_2"),
    attendancePolicyId: getField(row, "attendancePolicyId", "AttendancePolicyId", "field_3"),
    shiftPatternId: getField(row, "shiftPatternId", "ShiftPatternId", "field_4"),
    weeklyOffPatternId: getField(row, "weeklyOffPatternId", "WeeklyOffPatternId", "WeeklyOffPatternId ", "field_5"),
    holidayCalendarId: getField(row, "holidayCalendarId", "HolidayCalendarId", "HolidayCalendarId ", "field_6"),
    legalEntityCode: getField(row, "legalEntityCode", "LegalEntityCode", "field_7"),
    locationCode: getField(row, "locationCode", "LocationCode", "field_8"),
    departmentCode: getField(row, "departmentCode", "DepartmentCode", "field_9"),
    workUnitCode: getField(row, "workUnitCode", "WorkUnitCode", "WorkUnitCode ", "field_10"),
    effectiveFrom: getField(row, "effectiveFrom", "EffectiveFrom", "field_11"),
    effectiveTo: getField(row, "effectiveTo", "EffectiveTo", "field_12"),
    assignedBy: getField(row, "assignedBy", "AssignedBy", "AssignedBy ", "field_13"),
    assignmentReason: getField(row, "assignmentReason", "AssignmentReason", "field_14"),
    isCurrent: getField(row, "isCurrent", "IsCurrent", "field_15") === true || getField(row, "isCurrent", "IsCurrent", "field_15") === "Yes" || getField(row, "isCurrent", "IsCurrent", "field_15") === "true",
    isActive: getField(row, "isActive", "IsActive", "field_16") === true || getField(row, "isActive", "IsActive", "field_16") === "Yes" || getField(row, "isActive", "IsActive", "field_16") === "true",
  };
}

function normalizeHistoryInput(history) {
  if (!history) return [];
  if (Array.isArray(history)) return history.map(normalizeHistoryRow);
  if (typeof history === "object") return [normalizeHistoryRow(history)];
  return [];
}

function EmployeePolicyHistoryPanel({ employeeId, history = [], loading = false, onClose }) {
  if (!employeeId) return null;

  const normalizedHistory = normalizeHistoryInput(history);

  return (
    <div style={overlayStyle}>
      <div style={panelStyle}>
        <div style={headerStyle}>
          <div>
            <h2 style={{ margin: 0 }}>Policy Assignment History</h2>
            <p style={smallTextStyle}>Employee: {employeeId}</p>
          </div>
          <button type="button" onClick={onClose} style={closeButtonStyle}>Close</button>
        </div>

        {loading && <div style={infoStyle}>Loading policy assignment history...</div>}

        {!loading && normalizedHistory.length === 0 && (
          <div style={emptyStyle}>No policy assignment history found.</div>
        )}

        {!loading && normalizedHistory.length > 0 && (
          <div style={tableWrapperStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Current</th>
                  <th style={thStyle}>Leave Policy</th>
                  <th style={thStyle}>Attendance</th>
                  <th style={thStyle}>Shift</th>
                  <th style={thStyle}>Weekly Off</th>
                  <th style={thStyle}>Holiday</th>
                  <th style={thStyle}>Legal Entity</th>
                  <th style={thStyle}>Location</th>
                  <th style={thStyle}>Department</th>
                  <th style={thStyle}>Work Unit</th>
                  <th style={thStyle}>Effective From</th>
                  <th style={thStyle}>Effective To</th>
                  <th style={thStyle}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {normalizedHistory.map((row) => (
                  <tr key={row.id || `${row.employeeCode}-${row.effectiveFrom}-${row.shiftPatternId}`}>
                    <td style={tdStyle}>{row.isCurrent ? "Yes" : "No"}</td>
                    <td style={tdStyle}>{row.leavePolicyId}</td>
                    <td style={tdStyle}>{row.attendancePolicyId}</td>
                    <td style={tdStyle}>{row.shiftPatternId}</td>
                    <td style={tdStyle}>{row.weeklyOffPatternId}</td>
                    <td style={tdStyle}>{row.holidayCalendarId}</td>
                    <td style={tdStyle}>{row.legalEntityCode}</td>
                    <td style={tdStyle}>{row.locationCode}</td>
                    <td style={tdStyle}>{row.departmentCode}</td>
                    <td style={tdStyle}>{row.workUnitCode}</td>
                    <td style={tdStyle}>{formatDate(row.effectiveFrom)}</td>
                    <td style={tdStyle}>{formatDate(row.effectiveTo)}</td>
                    <td style={tdStyle}>{row.assignmentReason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const overlayStyle = { position: "fixed", inset: 0, backgroundColor: "rgba(15, 23, 42, 0.45)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center", padding: "20px" };
const panelStyle = { backgroundColor: "var(--fm-card)", borderRadius: "14px", padding: "20px", width: "min(1300px, 96vw)", maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 50px rgba(15, 23, 42, 0.35)" };
const headerStyle = { display: "flex", justifyContent: "space-between", gap: "16px", alignItems: "center", marginBottom: "16px" };
const smallTextStyle = { margin: "6px 0 0", color: "var(--fm-muted)" };
const closeButtonStyle = { backgroundColor: "var(--fm-card)", border: "1px solid var(--fm-border)", borderRadius: "8px", padding: "9px 14px", fontWeight: 700, cursor: "pointer" };
const infoStyle = { border: "1px solid #bfdbfe", backgroundColor: "#eff6ff", color: "#1e40af", borderRadius: "10px", padding: "12px", fontWeight: 700 };
const emptyStyle = { border: "1px solid var(--fm-border)", backgroundColor: "#f8fafc", color: "var(--fm-muted)", borderRadius: "10px", padding: "18px", textAlign: "center" };
const tableWrapperStyle = { overflowX: "auto" };
const tableStyle = { width: "100%", borderCollapse: "collapse", minWidth: "1200px", fontSize: "13px" };
const thStyle = { textAlign: "left", borderBottom: "1px solid var(--fm-border)", padding: "10px", backgroundColor: "#f8fafc", whiteSpace: "nowrap" };
const tdStyle = { borderBottom: "1px solid var(--fm-border)", padding: "10px", whiteSpace: "nowrap" };

export default EmployeePolicyHistoryPanel;
